import { Component } from '@angular/core';
import {
  DialogOption,
  DialogColor,
  DialogIcon,
  ModalButtonType,
  ModalButtonColor,
  ModalButton,
  AbstractBlModal,
  ModalSize,
  ModalReason,
  BlDialogService,
  BlModalService
} from '@blcloud/bl-ng-ui-component';
import { IFileManageInfo } from '@blcloud/bl-datamodel';
import { IProfileDataSelectModal, ITableData } from './profile-data-select-modal.interface';
import {
  DateTimeUtils, DateTimeOutput, StringUtils,
  DateTimePadding, DateSeparator, DateEra
} from '@blcloud/bl-common';
import {
  isNil as _isNil,
  cloneDeep as _cloneDeep,
  map as _map,
  some as _some,
  isEmpty as _isEmpty,
  tail as _tail,
  padStart as _padStart,
  padEnd as _padEnd,
} from 'lodash';
import { MemoTargetDivArray, MemoTargetDiv } from '@blcloud/bl-datamodel/enum/bizcmn/memo-target-div';
import { MemoImportantDiv } from '@blcloud/bl-datamodel/enum/bizcmn/memo-important-div';
import { FixedFormatMemoModalComponent } from '../fixed-format-memo-modal/fixed-format-memo-modal.component';
import { SexDivArray } from '@blcloud/bl-datamodel/enum/common/sex-div';
import { ProfileEntryLinkOptVcleStatusDiv } from '@blcloud/bl-datamodel/enum/profile/profile-entry-link-opt-vcle-status-div';

/**
 * 採用データ選択
 */
@Component({
  templateUrl: './profile-data-select-modal.component.html',
  styleUrls: ['./profile-data-select-modal.component.scss'],
})
export class ProfileDataSelectModalComponent extends AbstractBlModal {

  /** データ */
  data: IProfileDataSelectModal;

  /** テーブルデータ */
  _tableData: Array<ITableData> = [];

  /** プロフィール登録情報固定値 */
  readonly PROFILE_SELECT = 0;

  /** 登録されている情報固定値 */
  readonly CUSTOMER_VEHICLE_SELECT = 1;

  /** データがない場合の固定メッセージ */
  readonly NO_DATA_MESSAGE = '（指定なし）';

  /** お客様名固定値 */
  private readonly CUSTOMER_NAME = 'お客様名';

  /** お客様名カナ固定値 */
  private readonly CUSTOMER_NAME_KANA = 'お客様名カナ';

  /** 電話番号固定値 */
  private readonly TEL = '電話番号';

  /** メールアドレス固定値 */
  private readonly EMAIL = 'メールアドレス';

  /** 郵便番号固定値 */
  private readonly POSTAL_CODE = '郵便番号';

  /** 住所固定値 */
  private readonly ADDRESS = '住所';

  /** 性別固定値 */
  private readonly SEX_DIV = '性別';

  /** 生年月日固定値 */
  private readonly BIRTH_DAY = '生年月日';

  /** ナンバープレート固定値 */
  private readonly NUMBER_PLATE = 'ナンバープレート';

  /** メーカー／車種固定値 */
  private readonly MAKER_MODEL = 'メーカー／車種';

  /** 車検満期日固定値 */
  private readonly VCLE_INSP_DUE_DATE = '車検満期日';

  /** DM送信区分固定値 */
  private readonly DM_SEND_DIV = 'DM送信区分';

  /** 顧客のみ更新フラグ */
  _customerOnly = false;

  /** 読み込み画像（プロフィール） */
  _imageProfile: IFileManageInfo;

  /** 読み込み画像チェック（プロフィール） */
  _isImageProfileCheck: boolean;

  /** 読み込み画像チェック活性（プロフィール） */
  _isImageProfileCheckDisabled: boolean;

  /** 読み込み画像（車両・1つ目） */
  _imageVehicleFst: IFileManageInfo;

  /** 読み込み画像チェック（車両・1つ目） */
  _isImageVehicleFstCheck: boolean;

  /** 読み込み画像チェック活性（車両・1つ目） */
  _isImageVehicleFstCheckDisabled: boolean;

  /** 読み込み画像（車両・2つ目） */
  _imageVehicleScd: IFileManageInfo;

  /** 読み込み画像チェック（車両・2つ目） */
  _isImageVehicleScdCheck: boolean;

  /** 読み込み画像チェック活性（車両・2つ目） */
  _isImageVehicleScdCheckDisabled: boolean;

  /** メモ */
  _memo: string;

  /** メモラジオ */
  _memoTargetDiv = MemoTargetDiv.Customer;

  /** 表示用メモ重要区分 */
  _displayMemoImportantDiv = false;

  /** メモ対象区分項目リスト */
  memoTargetDivItemList = MemoTargetDivArray.filter(val => {
    return MemoTargetDiv.Deals !== val.value;
  });

  /** ダイアログボタン設定 */
  private dialogButtonSetting = {
    ok: { color: ModalButtonColor.None, label: '末尾に挿入' },
    any: { color: ModalButtonColor.None, label: '上書き' },
    cancel: { color: ModalButtonColor.None, label: 'キャンセル' }
  };

  /** ダイアログオプション */
  private DialogOption: DialogOption = {
    title: '確認',
    color: DialogColor.Primary,
    icon: DialogIcon.Question,
    buttonType: ModalButtonType.OkAnyCancel,
    buttonSetting: this.dialogButtonSetting,
    focus: ModalButton.Cancel
  };

  /**
   * コンストラクタ
   */
  constructor(
    private dialogService: BlDialogService,
    private modalService: BlModalService,
  ) {
    super();
  }

  /**
   * モーダルウィンドウ表示後イベントハンドラ
   *
   * モーダルウィンドウが表示後に初期データが設定された後にコールされます。
   */
  onShown() {
    const profileEntry = _cloneDeep(this.data.profileEntry);
    const profileEntryVehicle = _cloneDeep(this.data.profileEntryVehicle);
    const profileEntryCustomerVehicleLinkOptDtl = _cloneDeep(this.data.profileEntryCustomerVehicleLinkOptDtl);
    const profileTel = !_isNil(profileEntry.contactInfoTel) ? profileEntry.contactInfoTel.contactInformation : null;
    // 電話番号の先頭はラジオにいれて、他をラベル表示する
    let topTel = null;
    let telList = [];
    if (!_isEmpty(profileEntryCustomerVehicleLinkOptDtl.contactInfoTelList)) {
      topTel = profileEntryCustomerVehicleLinkOptDtl.contactInfoTelList[0].contactInformation;
      telList = _tail(profileEntryCustomerVehicleLinkOptDtl.contactInfoTelList).map(item => item.contactInformation);
    }

    const profileEmail = !_isNil(profileEntry.contactInfoEmail) ? profileEntry.contactInfoEmail.contactInformation : null;
    // メールアドレスの先頭はラジオにいれて、他をラベル表示する
    let topEmail = null;
    let emailList = [];
    if (!_isEmpty(profileEntryCustomerVehicleLinkOptDtl.contactInfoEmailList)) {
      topEmail = profileEntryCustomerVehicleLinkOptDtl.contactInfoEmailList[0].contactInformation;
      emailList = _tail(profileEntryCustomerVehicleLinkOptDtl.contactInfoEmailList).map(item => item.contactInformation);
    }

    this._tableData.push(
      {
        title: this.CUSTOMER_NAME,
        profileEntryValue: this.createItemLabel([
          profileEntry.customerFamilyName,
          profileEntry.customerGivenName]
        ),
        selectValue: 0,
        customerVehicleValue: this.createItemLabel([
          profileEntryCustomerVehicleLinkOptDtl.customerFamilyName,
          profileEntryCustomerVehicleLinkOptDtl.customerGivenName]
        ),
      },
      {
        title: this.CUSTOMER_NAME_KANA,
        profileEntryValue: this.createItemLabel([
          profileEntry.customerFamilyNameKana,
          profileEntry.customerGivenNameKana]
        ),
        selectValue: 0,
        customerVehicleValue: this.createItemLabel([
          profileEntryCustomerVehicleLinkOptDtl.customerFamilyNameKana,
          profileEntryCustomerVehicleLinkOptDtl.customerGivenNameKana]
        ),
      },
      {
        title: this.TEL,
        profileEntryValue: this.createItemLabel([profileTel]),
        profileEntryEtcValue: ['※現在登録されている情報に電話番号を追加します', '※登録済みと全く同じ場合は追加しません。'],
        selectValue: 0,
        customerVehicleValue: this.createItemLabel([topTel]),
        customerVehicleEtcValue: telList,
      },
      {
        title: this.EMAIL,
        profileEntryValue: this.createItemLabel([profileEmail]),
        profileEntryEtcValue: ['※現在登録されている情報にメールアドレスを追加します', '※登録済みと全く同じ場合は追加しません。'],
        selectValue: 0,
        customerVehicleValue: this.createItemLabel([topEmail]),
        customerVehicleEtcValue: emailList,
      },
      {
        title: this.POSTAL_CODE,
        profileEntryValue: this.createItemLabel([profileEntry.postalCode]),
        selectValue: 0,
        customerVehicleValue: this.createItemLabel([profileEntryCustomerVehicleLinkOptDtl.postalCode]),
      },
      {
        title: this.ADDRESS,
        profileEntryValue: this.createItemLabel([
          profileEntry.customerAddressStateCity,
          profileEntry.customerAddressStreet,
          profileEntry.customerAddressBuildingName,
        ]),
        selectValue: 0,
        customerVehicleValue: this.createItemLabel([
          profileEntryCustomerVehicleLinkOptDtl.customerAddressStateCity,
          profileEntryCustomerVehicleLinkOptDtl.customerAddressStreet,
          profileEntryCustomerVehicleLinkOptDtl.customerAddressBuildingName,
        ]),
      },
      {
        title: this.SEX_DIV,
        profileEntryValue: SexDivArray.filter(r => {
          return r.value === profileEntry.sexDiv;
        })[0].text,
        selectValue: 0,
        customerVehicleValue: SexDivArray.filter(r => {
          return r.value === profileEntryCustomerVehicleLinkOptDtl.sexDiv;
        })[0].text,
      },
      {
        title: this.BIRTH_DAY,
        profileEntryValue: this.dateTimeFormat(profileEntry.birthDay),
        selectValue: 0,
        customerVehicleValue: this.dateTimeFormat(profileEntryCustomerVehicleLinkOptDtl.birthDay),
      },
    );
    // 車両系
    if (profileEntryCustomerVehicleLinkOptDtl.profileEntryLinkOptVcleStatusDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
      this._customerOnly = true;
    } else {
      this._tableData.push(
        {
          title: this.NUMBER_PLATE,
          profileEntryValue: this.createItemLabelNumPlate(
            profileEntryVehicle.vcleInspRegistOfficeName,
            profileEntryVehicle.numPlateClassNum,
            profileEntryVehicle.numPlateKana,
            profileEntryVehicle.numPlateSeriesSpecifNum,
          ),
          selectValue: 0,
          customerVehicleValue: this.createItemLabelNumPlate(
            profileEntryCustomerVehicleLinkOptDtl.vcleInspRegistOfficeName,
            profileEntryCustomerVehicleLinkOptDtl.numPlateClassNum,
            profileEntryCustomerVehicleLinkOptDtl.numPlateKana,
            profileEntryCustomerVehicleLinkOptDtl.numPlateSeriesSpecifNum,
          ),
        },
        {
          title: this.MAKER_MODEL,
          profileEntryValue: this.createItemLabelSlash([
            profileEntryVehicle.blVehicleMakerFullWFixNm,
            profileEntryVehicle.vehicleModelFixFullWName
          ]),
          selectValue: 0,
          customerVehicleValue: this.createItemLabelSlash([
            profileEntryCustomerVehicleLinkOptDtl.blVehicleMakerFullWFixNm,
            profileEntryCustomerVehicleLinkOptDtl.vehicleModelFixFullWName
          ]),
        },
        {
          title: this.VCLE_INSP_DUE_DATE,
          profileEntryValue: this.dateTimeFormat(profileEntryVehicle.vcleInspDueDate),
          selectValue: 0,
          customerVehicleValue: this.dateTimeFormat(profileEntryCustomerVehicleLinkOptDtl.vcleInspDueDate),
        },
      );
    }
    this._tableData.push(
      // DM送信区分(プロフィール登録の物を固定で使用するので選択させない。表示するだけ)
      {
        title: this.DM_SEND_DIV,
        profileEntryValue: profileEntry.dmSendDivEnumName,
        selectValue: 0,
        customerVehicleValue: '　',
        customerVehicleDisabled: true,
      },
    );
    this._imageProfile = profileEntryVehicle.vcleInspCertifImageFileMgt;
    this._imageVehicleFst = profileEntryCustomerVehicleLinkOptDtl.attachFileInfoList[0];
    this._imageVehicleScd = profileEntryCustomerVehicleLinkOptDtl.attachFileInfoList[1];
    // 全ての画像がない場合(車両画像二つ目は、一つ目がないと存在しないはず)
    if (_isEmpty(this._imageProfile) && _isEmpty(this._imageVehicleFst)) {
      // 念のため全部チェック外して非活性
      this._isImageProfileCheck = false;
      this._isImageProfileCheckDisabled = true;
      this._isImageVehicleFstCheck = false;
      this._isImageVehicleFstCheckDisabled = true;
      this._isImageVehicleScdCheck = false;
      this._isImageVehicleScdCheckDisabled = true;
    } else if (!_isEmpty(this._imageProfile) && _isEmpty(this._imageVehicleFst)) {
      // プロフィールのみ存在
      this._isImageProfileCheck = true;
      this._isImageProfileCheckDisabled = false;
      this._isImageVehicleFstCheck = false;
      this._isImageVehicleFstCheckDisabled = true;
      this._isImageVehicleScdCheck = false;
      this._isImageVehicleScdCheckDisabled = true;
    } else if (_isEmpty(this._imageProfile) && !_isEmpty(this._imageVehicleFst)) {
      // 車両画像のみ存在
      this._isImageProfileCheck = false;
      this._isImageProfileCheckDisabled = true;
      this._isImageVehicleFstCheck = true;
      this._isImageVehicleFstCheckDisabled = true;
      if (_isEmpty(this._imageVehicleScd)) {
        this._isImageVehicleScdCheck = false;
        this._isImageVehicleScdCheckDisabled = true;
      } else {
        this._isImageVehicleScdCheck = true;
        this._isImageVehicleScdCheckDisabled = true;
      }
    } else {
      // プロフィール画像もあるし車両画像も存在する
      this._isImageProfileCheck = true;
      this._isImageVehicleFstCheck = true;
      this._isImageVehicleScdCheck = true;
      // プロフィール画像が車両画像一つ目と同じ画像
      if (this._imageProfile.fileManageId === this._imageVehicleFst.fileManageId) {
        this._isImageProfileCheck = false;
        this._isImageProfileCheckDisabled = true;
        this._isImageVehicleFstCheckDisabled = true;
        if (!_isEmpty(this._imageVehicleScd)) {
          this._isImageVehicleScdCheckDisabled = true;
        }
      }
      // プロフィール画像が車両画像二つ目と同じ画像
      if (_isEmpty(this._imageVehicleScd)) {
        this._isImageVehicleScdCheck = false;
        this._isImageVehicleScdCheckDisabled = true;
      } else if (this._imageProfile.fileManageId === this._imageVehicleScd.fileManageId) {
        this._isImageProfileCheck = false;
        this._isImageProfileCheckDisabled = true;
        this._isImageVehicleFstCheckDisabled = true;
        this._isImageVehicleScdCheckDisabled = true;
      }
    }
    // 顧客のみ更新は念のためプロフィール側を外す
    if (this._customerOnly) {
      this._isImageProfileCheck = false;
      this._isImageProfileCheckDisabled = true;
    }

    this._memo = !_isNil(profileEntry.messageNotices) ? profileEntry.messageNotices : '';
    // セットされた値をみて文字の設定を変える
    _map(this._tableData, data => {
      if (data.profileEntryValue === this.NO_DATA_MESSAGE) {
        data.profileEntryNoValue = true;
        if (data.customerVehicleValue !== this.NO_DATA_MESSAGE) {
          data.selectValue = this.CUSTOMER_VEHICLE_SELECT;
        }
      }
      if (data.customerVehicleValue === this.NO_DATA_MESSAGE) {
        data.customerVehicleNoValue = true;
      }
    });
  }


  // --------------------------------------------------
  // AbstractModal Override

  /**
   * @override
   */
  getResults() {
    const profileEntry = _cloneDeep(this.data.profileEntry);
    const profileEntryVehicle = _cloneDeep(this.data.profileEntryVehicle);
    const result = _cloneDeep(this.data.profileEntryCustomerVehicleLinkOptDtl);
    _map(this._tableData, data => {
      // お客様名
      if (data.title === this.CUSTOMER_NAME && data.selectValue === this.PROFILE_SELECT) {
        result.customerFamilyName = profileEntry.customerFamilyName;
        result.customerGivenName = profileEntry.customerGivenName;
      }
      // お客様名カナ
      if (data.title === this.CUSTOMER_NAME_KANA && data.selectValue === this.PROFILE_SELECT) {
        result.customerFamilyNameKana = profileEntry.customerFamilyNameKana;
        result.customerGivenNameKana = profileEntry.customerGivenNameKana;
      }
      // 電話番号
      if (data.title === this.TEL && data.selectValue === this.PROFILE_SELECT) {
        result.contactInfoTelList = [profileEntry.contactInfoTel];
        result.phoneNumber = profileEntry.phoneNumber;
      }
      // メールアドレス
      if (data.title === this.EMAIL && data.selectValue === this.PROFILE_SELECT) {
        result.contactInfoEmailList = [profileEntry.contactInfoEmail];
        result.emailAddress = profileEntry.emailAddress;
      }
      // 郵便番号
      if (data.title === this.POSTAL_CODE && data.selectValue === this.PROFILE_SELECT) {
        result.postalCode = profileEntry.postalCode;
      }
      // 住所
      if (data.title === this.ADDRESS && data.selectValue === this.PROFILE_SELECT) {
        result.customerAddressStateCity = profileEntry.customerAddressStateCity;
        result.customerAddressStreet = profileEntry.customerAddressStreet;
        result.customerAddressBuildingName = profileEntry.customerAddressBuildingName;
      }
      // 性別
      if (data.title === this.SEX_DIV && data.selectValue === this.PROFILE_SELECT) {
        result.sexDiv = profileEntry.sexDiv;
      }
      // 生年月日
      if (data.title === this.BIRTH_DAY && data.selectValue === this.PROFILE_SELECT) {
        result.birthDay = profileEntry.birthDay;
      }
      // ナンバープレート
      if (data.title === this.NUMBER_PLATE && data.selectValue === this.PROFILE_SELECT) {
        result.vcleInspRegistOfficeName = profileEntryVehicle.vcleInspRegistOfficeName;
        result.numPlateClassNum = profileEntryVehicle.numPlateClassNum;
        result.numPlateKana = profileEntryVehicle.numPlateKana;
        result.numPlateSeriesSpecifNum = profileEntryVehicle.numPlateSeriesSpecifNum;
      }
      // メーカー／車種
      if (data.title === this.MAKER_MODEL && data.selectValue === this.PROFILE_SELECT) {
        result.airisVehicleMakerCd = profileEntryVehicle.airisVehicleMakerCd;
        result.vehicleName = profileEntryVehicle.vehicleName;
        result.blVehicleMakerFixCd = profileEntryVehicle.blVehicleMakerFixCd;
        result.blVehicleMakerFullWFixNm = profileEntryVehicle.blVehicleMakerFullWFixNm;
        result.vehicleModelFixCd = profileEntryVehicle.vehicleModelFixCd;
        result.vehicleModelSubCd = profileEntryVehicle.vehicleModelSubCd;
        result.vehicleModelGrpCd = profileEntryVehicle.vehicleModelGrpCd;
        result.vehicleModelFixFullWName = profileEntryVehicle.vehicleModelFixFullWName;
      }
      // 車検満期日
      if (data.title === this.VCLE_INSP_DUE_DATE && data.selectValue === this.PROFILE_SELECT) {
        result.vcleInspDueDate = profileEntryVehicle.vcleInspDueDate;
      }
      // DM送信区分
      if (data.title === this.DM_SEND_DIV && data.selectValue === this.PROFILE_SELECT) {
        result.dmSendDiv = profileEntry.dmSendDiv;
      }
    });
    // 車検証画像
    const newFileInfo = [];
    if (this._isImageProfileCheck) {
      newFileInfo.push(profileEntryVehicle.vcleInspCertifImageFileMgt);
    }
    if (this._isImageVehicleFstCheck) {
      newFileInfo.push(result.attachFileInfoList[0]);
    }
    if (this._isImageVehicleScdCheck) {
      newFileInfo.push(result.attachFileInfoList[1]);
    }
    result.attachFileInfoList = newFileInfo;
    // メモ対象区分
    result.memoTargetDiv = this._memoTargetDiv;
    // メモ重要区分
    result.memoImportantDiv = this._displayMemoImportantDiv ? MemoImportantDiv.Important : MemoImportantDiv.Normal;
    // 商談メモ本文
    result.dealsMemoText = !_isEmpty(this._memo) ? this._memo : null;
    return result;
  }

  /**
   * 文字結合イベント
   * @param values
   */
  private createItemLabel(values: string[]) {
    if (_some(values, value => !_isNil(value))) {
      return StringUtils.concatWith('　', ...values);
    } else {
      return this.NO_DATA_MESSAGE;
    }
  }

  /**
   * 文字結合イベント(ナンバープレート)
   * @param values
   */
  private createItemLabelNumPlate(
    vcleInspRegistOfficeName: string,
    numPlateClassNum: string,
    numPlateKana: string,
    numPlateSeriesSpecifNum: number,
  ) {
    if (_isNil(vcleInspRegistOfficeName)
      && _isNil(numPlateClassNum)
      && _isNil(numPlateKana)
      && (numPlateSeriesSpecifNum === 0 || _isNil(numPlateSeriesSpecifNum))
    ) {
      return this.NO_DATA_MESSAGE;
    } else {
      let setNumPlateSeriesSpecifNum = String(numPlateSeriesSpecifNum);
      if (numPlateSeriesSpecifNum === 0 || _isNil(numPlateSeriesSpecifNum)) {
        setNumPlateSeriesSpecifNum = '';
      }
      const values = [
        _padEnd(vcleInspRegistOfficeName, 4, '　'),
        _padEnd(numPlateClassNum, 3, ' '),
        _padEnd(numPlateKana, 1, '　'),
        _padStart(setNumPlateSeriesSpecifNum, 4, ' '),
      ];
      return StringUtils.concatWithSpace(...values);
    }
  }

  /**
   * 文字結合イベント(全角スラッシュ)
   * @param values
   */
  private createItemLabelSlash(values: string[]) {
    if (_some(values, value => !_isNil(value))) {
      return StringUtils.concatWith('／', ...values);
    } else {
      return this.NO_DATA_MESSAGE;
    }
  }

  /**
   * 日付フォーマットイベント
   * @param values
   */
  private dateTimeFormat(value: string) {
    if (!_isNil(value)) {
      return DateTimeUtils.format(
        value, DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
      );
    } else {
      return this.NO_DATA_MESSAGE;
    }
  }

  /**
   * @override
   */
  defaultOption() {
    return {
      size: ModalSize.Large,
    };
  }

  onChangeSelectValue(select: number, index: number) {
    if (select === this.PROFILE_SELECT) {
      this._tableData[index].selectValue = this.PROFILE_SELECT;
    } else if (select === this.CUSTOMER_VEHICLE_SELECT) {
      this._tableData[index].selectValue = this.CUSTOMER_VEHICLE_SELECT;
    }
  }

  /**
   * @ignore
   * 車検証画像(プロフィール)チェックボックス変更イベント
   */
  onChangeImageProfile(event) {
    this._isImageProfileCheck = event;
  }

  /**
   * @ignore
   * 車検証画像(車両・1つ目)チェックボックス変更イベント
   */
  onChangeImageVehicleFst(event) {
    this._isImageVehicleFstCheck = event;
  }

  /**
   * @ignore
   * 車検証画像(車両・2つ目)チェックボックス変更イベント
   */
  onChangeImageVehicleScd(event) {
    this._isImageVehicleScdCheck = event;
  }

  /**
   * メモ対象区分変更イベント
   * @param event
   */
  onChangeMemoTargetDiv(event) {
    this._memoTargetDiv = event;
  }

  /**
   * 申し送り事項本文（テキストエリア）
   * @param event
   */
  onChangeMemo(event): void {
    this._memo = event;
  }

  /**
   * メモ重要区分
   */
  onClickNarrowDown(event) {
    this._displayMemoImportantDiv = event;
  }

  /**
   * 商談メモ定型文モーダルを表示
   */
  onClickShowFixedFormatMemoModal() {
    this.modalService.show(FixedFormatMemoModalComponent)
      .subscribe(res => {
        const ret = res.getResults();
        if (res.reason === ModalReason.Done) {
          this.dialogService.custom('選択した定型文でメモ本文を上書きしますか？<br>それとも末尾に挿入しますか？', this.DialogOption
        ).subscribe(
            modalRef => {
              modalRef.hide();
              if (modalRef.reason === ModalReason.Done) {
                this._memo = this._memo + ret.dealsMemoFixedFormat;
              } else if (modalRef.reason === ModalReason.Any) {
                this._memo = ret.dealsMemoFixedFormat;
              }
              // 末尾に挿入する可能性があるので200文字以上はカットする
              this._memo = this._memo.substring(0, 200);
            }
          );
        }
        res.hide();
    });
  }

  /**
   * モーダル終了イベント
   * @param reason　モーダル終了理由
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      if (this._isImageProfileCheck && this._isImageVehicleFstCheck && this._isImageVehicleScdCheck) {
        this.dialogService.warn('選択出来る画像は2つまでです。').subscribe(
          modalRef => {
            modalRef.hide();
          }
        );
      } else {
        let target = '顧客車両情報';
        if (this._customerOnly) {
          target = '顧客情報';
        }
        const message = this.getDialogMessage();
        if (message) {
          this.dialogService.info(message).subscribe(
            modalInfo => {
              modalInfo.hide();
              this.dialogService.confirm('選択した情報で' + target + 'を上書きします。<br>よろしいですか？').subscribe(
                modalRef => {
                  modalRef.hide();
                  if (modalRef.reason === ModalReason.Done) {
                    this.emitDismissEvent(reason);
                  }
                }
              );
            }
          );
        } else {
          this.dialogService.confirm('選択した情報で' + target + 'を上書きします。<br>よろしいですか？').subscribe(
            modalRef => {
              modalRef.hide();
              if (modalRef.reason === ModalReason.Done) {
                this.emitDismissEvent(reason);
              }
            }
          );
        }
      }
    } else {
      this.emitDismissEvent(reason);
    }
  }

  /**
   * モーダル終了時に出すメッセージを取得する
   */
  private getDialogMessage(): string {
    const profileEntryVehicle = _cloneDeep(this.data.profileEntryVehicle);
    const result = _cloneDeep(this.data.profileEntryCustomerVehicleLinkOptDtl);
    let message = null;
    _map(this._tableData, data => {
      // メーカー／車種でプロフィールが選択されている、既存顧客車両にデータがある、既存顧客車両と同じではない場合、メッセージセット
      if (data.title === this.MAKER_MODEL && data.selectValue === this.PROFILE_SELECT) {
        if (
          data.customerVehicleValue !== this.NO_DATA_MESSAGE
          && (
            result.airisVehicleMakerCd !== profileEntryVehicle.airisVehicleMakerCd
            || result.vehicleName !== profileEntryVehicle.vehicleName
            || result.blVehicleMakerFixCd !== profileEntryVehicle.blVehicleMakerFixCd
            || result.blVehicleMakerFullWFixNm !== profileEntryVehicle.blVehicleMakerFullWFixNm
            || result.vehicleModelFixCd !== profileEntryVehicle.vehicleModelFixCd
            || result.vehicleModelSubCd !== profileEntryVehicle.vehicleModelSubCd
            || result.vehicleModelGrpCd !== profileEntryVehicle.vehicleModelGrpCd
            || result.vehicleModelFixFullWName !== profileEntryVehicle.vehicleModelFixFullWName
          )
        ) {
          message = '「メーカー/車種」を変更します。'
          + '<br>型式指定番号、類別区分番号、型式　などの情報は変わりませんので、'
          + '<br>更新後は必ず車両画面にて各情報の確認を行い、'
          + '<br>必要であれば修正や項目のクリアをお願い致します。';
        }
      }
    });
    return message;
  }
}



// WEBPACK FOOTER //
// ./src/app/page/profile-entries-page/profile-data-select-modal/profile-data-select-modal.component.ts